import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Upload, CheckCircle, RotateCcw } from 'lucide-react';

import Button from '../../components/ui/Button';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';
import {
  Container, StickyHeader, Title, Card, DropZone, DropText, FileName,
  SummaryGrid, SummaryCard, SummaryValue, SummaryLabel, Footer, HistoryTable, SectionTitle,
} from '../../components/import/ImportStyles';
import {
  getJobArchitectureTemplate,
  validateJobArchitectureImport,
  executeJobArchitectureImport,
} from '../../services/jobArchitectureImportService';

// İş mimarisi toplu kurulumu (spec 2026-10-27 §7.9) — PerformanceImportPage aynası: dosya seçilince
// otomatik doğrulanır; kaydet dosyayı YENİDEN gönderir (sunucu geçici dosya saklamaz).
function translateRowError(t, key) {
  return t(`responseErrors.${key}`, { defaultValue: t(key, { defaultValue: key }) });
}

export default function JobArchitectureImportPage() {
  const { t } = useTranslation();
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const fileInputRef = useRef(null);

  const sheets = useMemo(() => result?.sheets ?? [], [result]);
  const totalRows = result?.totalRows ?? 0;
  const validRows = result?.validRows ?? 0;
  const errorRows = result?.errorRows ?? 0;
  const issues = useMemo(() => sheets.flatMap((s) => [...(s.errors ?? []), ...(s.warnings ?? [])]), [sheets]);
  const sheetLabel = useCallback((key) => t(`jobArchitectureImport.sheets.${key}`), [t]);

  const handleDownloadTemplate = useCallback(async () => {
    const res = await getJobArchitectureTemplate();
    // 403/hata durumunda res.data gerçek bir Blob DEĞİL, EMPTY_RESPONSE gövdesidir.
    if (!(res?.data instanceof Blob) || res.data.size < 100) return;
    const url = window.URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'flexbee-is-mimarisi-sablonu.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }, []);

  const handleFileChosen = useCallback(async (file) => {
    if (!file || !file.name.endsWith('.xlsx')) return;
    setFileName(file.name);
    setSelectedFile(file);
    setSaved(null);
    setBusy(true);
    try {
      const res = await validateJobArchitectureImport(file);
      if (res?.data?.status) setResult(res.data.data);
    } catch {
      // api.js interceptor toast'ladı; finally busy'yi geri açsın diye yakalıyoruz.
    } finally {
      setBusy(false);
    }
  }, []);

  const handleInputChange = useCallback((e) => {
    handleFileChosen(e.target.files?.[0]);
    e.target.value = '';
  }, [handleFileChosen]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    handleFileChosen(e.dataTransfer.files?.[0]);
  }, [handleFileChosen, busy]);

  const handleSave = useCallback(async () => {
    if (!selectedFile) return;
    setBusy(true);
    try {
      const res = await executeJobArchitectureImport(selectedFile);
      if (res?.data?.status) {
        setResult(res.data.data);
        setSaved(res.data.data.validRows ?? 0);
      }
    } catch {
      // Handled by interceptor
    } finally {
      setBusy(false);
    }
  }, [selectedFile]);

  const handleReset = useCallback(() => {
    setResult(null);
    setFileName('');
    setSelectedFile(null);
    setSaved(null);
  }, []);

  return (
    <Container>
      <StickyHeader>
        <Title>{t('jobArchitectureImport.title')}</Title>
      </StickyHeader>

      <Card>
        <SectionTitle>{t('jobArchitectureImport.howItWorks')}</SectionTitle>
        <p style={{ fontFamily: 'Nunito Sans', fontSize: 14, color: '#667085', lineHeight: 1.6 }}>
          {t('jobArchitectureImport.description')}
        </p>
        <Footer>
          <Button variant="outline" onClick={handleDownloadTemplate}>
            <Download size={16} style={{ marginRight: 8 }} />
            {t('jobArchitectureImport.downloadTemplate')}
          </Button>
          <div />
        </Footer>
      </Card>

      <Card>
        <SectionTitle>{t('jobArchitectureImport.uploadFile')}</SectionTitle>

        <DropZone
          $dragOver={dragOver}
          onClick={() => { if (!busy) fileInputRef.current?.click(); }}
          onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          style={busy ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        >
          <Upload size={32} color="#667085" />
          <DropText>
            {fileName ? <FileName>{fileName}</FileName> : t('jobArchitectureImport.dropzone')}
          </DropText>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            data-testid="job-architecture-import-file"
            disabled={busy}
            style={{ display: 'none' }}
            onChange={handleInputChange}
          />
        </DropZone>

        {result && (
          <>
            <SummaryGrid style={{ marginTop: 20 }}>
              <SummaryCard $color="#F8FAFC">
                <SummaryValue>{totalRows}</SummaryValue>
                <SummaryLabel>{t('jobArchitectureImport.totalRows')}</SummaryLabel>
              </SummaryCard>
              <SummaryCard $color="#F0FDF4">
                <SummaryValue>{validRows}</SummaryValue>
                <SummaryLabel>{t('jobArchitectureImport.willBeSaved')}</SummaryLabel>
              </SummaryCard>
              <SummaryCard $color="#FEF2F2">
                <SummaryValue>{errorRows}</SummaryValue>
                <SummaryLabel>{t('jobArchitectureImport.hasError')}</SummaryLabel>
              </SummaryCard>
            </SummaryGrid>

            <HistoryTable style={{ marginTop: 16 }} data-testid="job-architecture-sheets">
              <thead>
                <tr>
                  <th>{t('jobArchitectureImport.sheet')}</th>
                  <th>{t('jobArchitectureImport.totalRows')}</th>
                  <th>{t('jobArchitectureImport.newRows')}</th>
                  <th>{t('jobArchitectureImport.updateRows')}</th>
                  <th>{t('jobArchitectureImport.hasError')}</th>
                </tr>
              </thead>
              <tbody>
                {sheets.map((s) => (
                  <tr key={s.sheet}>
                    <td>{sheetLabel(s.sheet)}</td>
                    <td>{s.totalRows}</td>
                    <td>{s.newRows}</td>
                    <td>{s.updateRows}</td>
                    <td>{s.errorRows}</td>
                  </tr>
                ))}
              </tbody>
            </HistoryTable>

            {issues.length > 0 && (
              <HistoryTable style={{ marginTop: 16 }}>
                <thead>
                  <tr>
                    <th>{t('jobArchitectureImport.sheet')}</th>
                    <th>{t('jobArchitectureImport.rowNumber')}</th>
                    <th>{t('jobArchitectureImport.column')}</th>
                    <th>{t('jobArchitectureImport.message')}</th>
                  </tr>
                </thead>
                <tbody>
                  {issues.map((e, i) => (
                    <tr key={`${e.sheet}-${e.row}-${e.column}-${i}`} style={{ background: e.severity === 'warning' ? '#FFFBEB' : '#FEF2F2' }}>
                      <td>{sheetLabel(e.sheet)}</td>
                      <td>{e.row > 0 ? e.row : '-'}</td>
                      <td>{e.column}</td>
                      <td>{translateRowError(t, e.message)}</td>
                    </tr>
                  ))}
                </tbody>
              </HistoryTable>
            )}
          </>
        )}

        <Footer>
          <div>
            {saved !== null && (
              <Tooltip text={t('jobArchitectureImport.resetTooltip')}>
                <IconButton onClick={handleReset}>
                  <RotateCcw size={16} />
                </IconButton>
              </Tooltip>
            )}
          </div>
          {saved === null && (
            <Button
              data-testid="job-architecture-import-submit"
              disabled={validRows === 0 || busy || !selectedFile}
              onClick={handleSave}
            >
              <CheckCircle size={16} style={{ marginRight: 8 }} />
              {`${t('jobArchitectureImport.save')} (${validRows})`}
            </Button>
          )}
        </Footer>

        {saved !== null && (
          <p style={{ fontFamily: 'Nunito Sans', fontSize: 14, color: '#667085', marginTop: 8 }}>
            <CheckCircle size={14} color="#16a34a" style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
            {t('jobArchitectureImport.savedSummary', { count: saved })}
          </p>
        )}
      </Card>
    </Container>
  );
}
